import { z } from 'zod';
/** The site's legal pages (doc 63). */
export const LegalKeySchema = z.enum(['impressum', 'privacy', 'terms']);
export const LEGAL_KEYS = LegalKeySchema.options;
export const LEGAL_TITLES = Object.freeze({
    impressum: 'Impressum',
    privacy: 'Privacy policy',
    terms: 'Terms of use',
});
/** A gap in the skeleton the owner still has to fill: `⟦your name⟧`. */
export const LEGAL_GAP = '⟦';
export const LEGAL_MAX_CHARS = 30_000;
/** Written, and no gap left. */
export function legalComplete(body) {
    return Boolean(body?.trim()) && !body?.includes(LEGAL_GAP);
}
export const LegalPageSchema = z.object({ key: LegalKeySchema, title: z.string(), body: z.string(), updatedAt: z.string() });
export const LegalPageResponseSchema = z.object({ page: LegalPageSchema });
/** GET /api/v1/legal — the complete pages, for the footer. */
export const LegalLinksResponseSchema = z.object({ pages: z.array(z.object({ key: LegalKeySchema, title: z.string() })) });
/** GET /api/v1/admin/legal — every page for the editor: its text (null = not written yet) and Couch's skeleton. */
export const AdminLegalPageSchema = z.object({
    key: LegalKeySchema,
    title: z.string(),
    body: z.string().nullable(),
    updatedAt: z.string().nullable(),
    complete: z.boolean(),
    template: z.string(),
});
export const AdminLegalPagesResponseSchema = z.object({ pages: z.array(AdminLegalPageSchema) });
export const SaveLegalPageRequestSchema = z.object({ body: z.string().max(LEGAL_MAX_CHARS) });
/**
 * Couch's skeletons: headings and gaps only, in the usual order for a German site. They are not legal advice — the owner
 * writes or checks the text.
 */
export const LEGAL_TEMPLATES = Object.freeze({
    impressum: `Impressum

Angaben gemäß § 5 DDG
⟦Vor- und Nachname⟧
⟦Straße und Hausnummer⟧
⟦PLZ und Ort⟧

Kontakt
E-Mail: ⟦E-Mail-Adresse⟧

Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV
⟦Vor- und Nachname, Anschrift wie oben⟧`,
    privacy: `Privacy policy

1. Who is responsible
⟦Name, address, e-mail — as in the Impressum⟧

2. What Couch stores, and why
- Your account: user name, display name and a hash of your password — to sign you in.
- A session cookie — only to keep you signed in; no tracking, no advertising cookies.
- What you create: workflows, their runs, previews (thumbnails) of the results, the machines you connect.
- Keys you choose to keep on Couch (e.g. a RunPod key) — encrypted.
- Server logs with IP addresses — for security and errors, kept ⟦how long⟧.
⟦anything else⟧

3. Where it is processed
⟦hosting provider, country⟧. Full-size results stay on your own machines.

4. Legal basis
⟦e.g. Art. 6 (1) (b) GDPR for the service you signed up for, Art. 6 (1) (f) GDPR for security logs⟧

5. Your rights
You can ask for access, correction, deletion, restriction, portability, and object (Art. 15–21 GDPR), and complain to
a data protection authority. ⟦contact for requests⟧`,
    terms: `Terms of use

1. Who may use Couch
You must be at least 18 years old.

2. Your content
You are responsible for what you create and share with Couch and for keeping the law. ⟦rules about forbidden content⟧

3. Cloud machines
Cloud providers bill you directly. Couch tries to stop idle machines but cannot guarantee it; check your provider's
console and bill yourself. Couch takes no liability for cloud costs.

4. The service
Couch is provided as it is, without guarantees of availability. ⟦limits of liability⟧

5. Ending
You can stop using Couch at any time. ⟦how accounts are deleted⟧

⟦date of these terms⟧`,
});
//# sourceMappingURL=legal.js.map